import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './ProfileModal.scss';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

let modalRootEl = null;
function getModalRoot() {
  if (!modalRootEl) {
    modalRootEl = document.createElement('div');
    modalRootEl.setAttribute('id', 'profile-modal-root');
    document.body.appendChild(modalRootEl);
  }
  return modalRootEl;
}

/**
 * Body-portal dialog that presents a member/alumni Profile over the
 * still-mounted roster page. Handles focus trapping, AT-hiding of the
 * background, body scroll locking (without losing scroll position), and
 * focus restoration to whatever activated it.
 */
export default function ProfileModal({ onClose, labelledBy, backgroundScrollY, children }) {
  const surfaceRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Scroll lock + AT-hide background + focus restoration — runs once per mount.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    const root = getModalRoot();

    const hiddenEls = [];
    Array.from(document.body.children).forEach((el) => {
      if (el === root) return;
      if (el.hasAttribute('aria-hidden')) return;
      el.setAttribute('aria-hidden', 'true');
      hiddenEls.push(el);
    });

    const scrollY = Number.isFinite(backgroundScrollY) ? backgroundScrollY : window.scrollY;
    const { style: bodyStyle } = document.body;
    const prevBodyStyle = {
      position: bodyStyle.position,
      top: bodyStyle.top,
      left: bodyStyle.left,
      right: bodyStyle.right,
      width: bodyStyle.width,
      overflow: bodyStyle.overflow,
    };
    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.left = '0';
    bodyStyle.right = '0';
    bodyStyle.width = '100%';
    bodyStyle.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      if (closeButtonRef.current) closeButtonRef.current.focus({ preventScroll: true });
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      hiddenEls.forEach((el) => el.removeAttribute('aria-hidden'));

      bodyStyle.position = prevBodyStyle.position;
      bodyStyle.top = prevBodyStyle.top;
      bodyStyle.left = prevBodyStyle.left;
      bodyStyle.right = prevBodyStyle.right;
      bodyStyle.width = prevBodyStyle.width;
      bodyStyle.overflow = prevBodyStyle.overflow;
      window.scrollTo(0, scrollY);

      const toRestore = previouslyFocusedRef.current;
      if (toRestore && typeof toRestore.focus === 'function' && document.contains(toRestore)) {
        toRestore.focus({ preventScroll: true });
      }
    };
  }, [backgroundScrollY]);

  // Escape-to-close + Tab/Shift+Tab focus trap.
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const node = surfaceRef.current;
      if (!node) return;
      const focusable = Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !node.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !node.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);

  function handleScrimMouseDown(event) {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }

  return ReactDOM.createPortal(
    <div className="ProfileModal-scrim" onMouseDown={handleScrimMouseDown}>
      <div
        className="ProfileModal-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        ref={surfaceRef}
      >
        <div className="ProfileModal-header">
          <button
            type="button"
            className="ProfileModal-close"
            onClick={onClose}
            ref={closeButtonRef}
          >
            <span className="ProfileModal-closeText">Close</span>
            <span className="ProfileModal-closeMark" aria-hidden="true" />
          </button>
        </div>
        <div className="ProfileModal-body">
          <div className="ProfileModal-content">{children}</div>
        </div>
      </div>
    </div>,
    getModalRoot()
  );
}
